import Loading from 'components/shared/Loading';
import { ReferenceCard } from './Reference';
import BlockQueryError from './BlockQueryError';
import { BlockResult, IResultFormat, IResultTypes, ResultStatus } from 'types';
import CustomChart from 'components/chart/CustomChart';
import Table from './Table';
import Typography from 'components/shared/Typography';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { cleanString } from 'utils';
import { Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useEditBlockResult, useFetchBlockReferences, useFetchBlockResult, useFetchChatHistory } from 'services';
import { Modal, Tooltip } from 'components/shared';
import { RegenerateBlockModal } from './RegenerateBlockModal';
import classNames from 'classnames';
import { BsChatDots } from 'react-icons/bs';
import { LuPencilLine, LuRefreshCcw } from 'react-icons/lu';
import { edit_note_icon } from 'assets/svg';
import { IoCheckmarkSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { Chat, ChatItem, IChatMessages } from './Chat';
import { loading_gif } from 'assets/gif';

interface BlockitemInterface {
  results: BlockResult[];
  result_status?: string;
  resultType?: IResultTypes;
  result_format?: string;
  isEditable: boolean;
  reportId: string;
  blockId: string;
  title: string;
}

export default function BlockItem(res: BlockitemInterface) {
  const navigate = useNavigate();
  const { result_status, result_format, reportId, blockId, results, title } = res;

  const [regenerateComment, setRegenerateComment] = useState('');
  const [isRedoing, setIsRedoing] = useState<boolean>(false);
  const [isUpdateBlockModalOpen, setIsUpdateBlockModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const timeUpdated = (results && results.length > 0 && results[0].updated) || new Date();

  const [isOpen, setIsChatOpen] = useState(false);
  const date = new Date(timeUpdated as string).toLocaleDateString();
  const time = new Date(timeUpdated as string).toLocaleTimeString();

  const customId = 'custom-toast-id';

  const {
    data: blockResult,
    isFetching: isFetchingBlock,
    error: isBlockError,
    isSuccess: isBlockSuccess,
    refetch,
  } = useFetchBlockResult(reportId, blockId, Boolean(results.length > 0), isRedoing, regenerateComment);

  useEffect(() => {
    if (results.length === 0) {
      refetch();
    }
  }, [results]);

  const block_result_status = blockResult?.data?.block?.result_status;

  let clean: any = null;
  let tableToRender: string | null = null;

  const [chatMessages, setChatMessages] = useState<ChatItem[]>([]);

  const [chatSessionId, setChatSessionId] = useState<string>('');

  const handleReferesh = () => {
    setIsRedoing(true);
    setTimeout(() => {
      refetch();
    }, 200);
    setIsUpdateBlockModalOpen(false);
  };

  const { editBlock } = useEditBlockResult();
  const handleEditBlock = async (id: string) => {
    setIsEditable(true);
    setIsUpdating(true);
  };

  const updateBlock = async (id: string) => {
    const updatedContent = document.getElementById(String(id))?.innerText;

    const resultId = document.getElementById(String(id))?.className.split(' ')[1] as string;

    const data = {
      content: String(updatedContent),
      reportId,
      blockId: String(id),
      resultId,
    };

    setIsEditable(false);
    editBlock(data, {
      onError: (error, variables, context) => {
        setIsUpdating(false);
        toast.error('Error updating report block', {
          position: 'top-center',
          toastId: customId,
          autoClose: 2000,
          theme: 'colored',
        });
      },
      onSuccess: (data, variables, context) => {
        setIsUpdating(false);
        toast.success('Report block successfully updated', {
          position: 'top-center',
          toastId: customId,
          autoClose: 1500,
          theme: 'colored',
        });
      },
    });
  };

  const toggleSidebar = () => {
    setIsChatOpen(!isOpen);
  };

  const primaryResultType = results.length > 0 && results[0]?.result_type;

  const computedResults = (blockResult && isBlockSuccess && blockResult?.data?.results) || results;

  const displayBlocks = isRedoing
    ? block_result_status !== ResultStatus.ERROR
    : computedResults && computedResults?.length > 0 && result_status !== 'error';

  const triggerReference = (!isRedoing && displayBlocks) || (isRedoing && isBlockSuccess && block_result_status === ResultStatus.SUCCESS);

  const {
    data: blockReferences,
    isFetching: isFetchingBlockReferences,
    error: isBlockReferencesError,
    refetch: refetchReference,
  } = useFetchBlockReferences(reportId, blockId, Boolean(triggerReference));

  const {
    data: chatHistory,
    isFetching: isFetchingChatHistory,
    error: ischatHistoryError,
  } = useFetchChatHistory(reportId, blockId, Boolean(setChatSessionId));

  useEffect(() => {
    if (isRedoing && blockResult) {
      refetchReference();
    }
  }, [isRedoing, blockResult, refetchReference]);

  useEffect(() => {
    if (chatHistory?.data?.messages?.length > 0) {
      setChatMessages(chatHistory?.data?.messages);
    }
  }, [chatHistory]);

  const blockQueryError = isRedoing
    ? isBlockError || (block_result_status === ResultStatus.ERROR && !isFetchingBlock)
    : result_status === ResultStatus.ERROR;

  const blockDisplaySuccess = isRedoing
    ? block_result_status !== ResultStatus.ERROR && !isFetchingBlock && !isBlockError
    : result_status !== ResultStatus.ERROR;

  return (
    <div className='flex w-full'>
      <div className='w-full'>
        {isUpdateBlockModalOpen && (
          <Modal
            onClose={() => setIsUpdateBlockModalOpen(false)}
            open={isUpdateBlockModalOpen}
            variant='full'
            children={
              <RegenerateBlockModal
                handleRefresh={handleReferesh}
                onClose={() => setIsUpdateBlockModalOpen(false)}
                setComment={setRegenerateComment}
                isFetchingBlock={isFetchingBlock}
              />
            }
          />
        )}{' '}
        {isOpen && <div className='fixed inset-0 bg-shade-black bg-opacity-50 z-30' onClick={toggleSidebar}></div>}
        <div
          className={`fixed top-0 right-0 h-full  bg-shade-white shadow-lg z-40 transform transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0 w-[40%]' : 'translate-x-full '
          }`}
        >
          <Chat
            report_id={reportId}
            block_id={blockId}
            // previous_response={result as string}
            previous_response={'testing chat functions'}
            onClose={toggleSidebar}
            // setChatMessages={setChatMessages}
            setChatSessionId={setChatSessionId}
            chatMessages={chatMessages}
            open={isOpen}
          />
        </div>
        <div className=''>
          <div className={classNames('text-left mb-2')}>
            <Typography variant='displaysm' weight='semibold' className='text-shade-black'>
              {title}{' '}
            </Typography>
          </div>
          <div className='flex justify-between items-center w-full'>
            <Typography variant='textsm'>
              Last updated {time} - {date}
            </Typography>

            <div className='flex gap-3 items-center'>
              {isEditable ? (
                <Tooltip text='Complete'>
                  <div
                    className={classNames(
                      'p-[18px] flex gap-2 hover:bg-grey-100 border-grey-200 border-[2px] rounded-md items-center justify-center hover:cursor-pointer',
                      {
                        'pointer-events-none bg-grey-200 opacity-30':
                          primaryResultType !== IResultTypes.TEXT ||
                          result_status === ResultStatus.ERROR ||
                          isBlockError ||
                          block_result_status === ResultStatus.ERROR ||
                          isFetchingBlock,
                      }
                    )}
                    onClick={() => updateBlock(blockId)}
                  >
                    <div aria-label='edit complete'>
                      {' '}
                      <IoCheckmarkSharp aria-disabled={isUpdating} />{' '}
                    </div>
                  </div>
                </Tooltip>
              ) : (
                <Tooltip text='Update'>
                  <div
                    className={classNames(
                      'p-[14px] flex gap-2 hover:bg-grey-100 border-grey-200 border-[2px] rounded-md items-center justify-center hover:cursor-pointer',
                      {
                        'pointer-events-none bg-grey-200 opacity-30':
                          primaryResultType !== IResultTypes.TEXT ||
                          result_format === IResultFormat.TABLE ||
                          result_status === ResultStatus.ERROR ||
                          isBlockError ||
                          block_result_status === ResultStatus.ERROR ||
                          isFetchingBlock,
                      }
                    )}
                    onClick={() => handleEditBlock(blockId)}
                  >
                    <div aria-label='edit'>
                      <img src={edit_note_icon} alt='edit' />
                    </div>
                  </div>
                </Tooltip>
              )}

              <Tooltip text='Regenerate'>
                <div
                  className={classNames(
                    'p-[18px] flex gap-2 border-grey-200 border-[2px] hover:bg-grey-100 rounded-md items-center justify-center hover:cursor-pointer',
                    {
                      'pointer-events-none opacity-30': isFetchingBlock,
                    }
                  )}
                  onClick={() => setIsUpdateBlockModalOpen(true)}
                >
                  <div aria-label='refresh'>
                    <LuRefreshCcw />
                  </div>
                </div>
              </Tooltip>
              <Tooltip text='Edit'>
                <div
                  className={classNames(
                    'p-[18px] flex gap-2 border-grey-200 border-[2px]  hover:bg-grey-100 rounded-md items-center justify-center hover:cursor-pointer',
                    {
                      'pointer-events-none bg-grey-200 opacity-30': isFetchingBlock,
                    }
                  )}
                  onClick={() => navigate(`/blocks/${reportId}/edit/${blockId}`)}
                >
                  <div aria-label='update'>
                    <LuPencilLine />
                  </div>
                </div>
              </Tooltip>
              <Tooltip text='Chat'>
                <div
                  className={classNames(
                    'p-[18px] flex gap-2 border-grey-200 border-[2px] hover:bg-grey-100 rounded-md items-center justify-center hover:cursor-pointer',
                    {
                      'pointer-events-none bg-grey-200 opacity-30':
                        result_status === ResultStatus.ERROR || isBlockError || block_result_status === ResultStatus.ERROR || isFetchingBlock,
                    }
                  )}
                  onClick={toggleSidebar}
                >
                  <div aria-label='chat'>
                    <BsChatDots />
                  </div>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        {blockQueryError && <BlockQueryError reportId={reportId} blockId={blockId} message={computedResults[0].content as string} />}
        {isFetchingBlock && (
          <div className='w-full flex justify-center items-center relative bg-grey-50 mt-20 animate-bounce rounded-lg'>
            <div className='spinner-box'>
              <div className='configure-border-1'>
                <div className='configure-core'></div>
              </div>
              <div className='configure-border-2'>
                <div className='configure-core'></div>
              </div>
            </div>
          </div>
        )}
        {/* {result_status === ResultStatus.ERROR && (
          <div className="my-[60px] py-[60px] flex justify-center items-center border-[1px] border-grey-100">
            <Typography variant="displaysm" weight="semibold" className="text-shade-black text-center">
              There was an unexpected error while retrieving block
            </Typography>
          </div>
        )} */}
        {blockDisplaySuccess && (
          <>
            <div>
              {displayBlocks &&
                computedResults.map((result: any, index: number) => (
                  <div key={index}>
                    <div className={classNames('w-full mt-10')}>
                      <div className={classNames(' text-justify')}>
                        {result.result_type === IResultTypes.TEXT && result.result_format !== IResultFormat.TABLE && (
                          <>
                            <Text
                              whiteSpace={'pre-line'}
                              borderWidth={isEditable ? '2px' : '0px'}
                              contentEditable={isEditable}
                              id={String(blockId)}
                              className={result.id}
                            >
                              {' '}
                              <MarkdownPreview
                                source={`${result.content}`}
                                wrapperElement={{
                                  'data-color-mode': 'light',
                                }}
                              />
                            </Text>
                          </>
                        )}
                      </div>

                      <div className={classNames('w-full flex justify-center')}>
                        {result.result_format === IResultFormat.CHART && result.result_type === IResultTypes.JSON_STRING && result.content && (
                          <>
                            <div className={classNames('border-[1px] border-grey-100 rounded-md p-4 w-full flex justify-center')}>
                              <CustomChart
                                chartData={JSON.parse(result.content as string)}
                                chartType={result.chart_type || 'bar_chart'}
                                title={title as string}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {result.result_format === IResultFormat.TABLE && (
                        <>
                          <Table data={cleanString(result.content) as any} />
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            <div className='mt-8'>
              {isBlockReferencesError && (
                <div className='flex justify-start items-center'>
                  <Typography variant='textmd' weight='semibold' className='text-shade-black text-left'>
                    There was an unexpected error while retrieving References
                  </Typography>
                </div>
              )}

              {isFetchingBlockReferences && !blockReferences && (
                <div className=''>
                  <Loading numberOfLoaders={1} className='bg-grey-200 !h-4' />
                </div>
              )}
              {blockReferences?.data?.references.length > 0 && (
                <div className='flex gap-4 mt-8'>
                  <ReferenceCard reference={blockReferences?.data} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
